import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import {
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  ListSubheader,
  makeStyles,
  MenuItem,
  Select,
  TextField,
  Theme,
  Typography,
} from '@material-ui/core';
import { Clear, ErrorOutline } from '@material-ui/icons';

import { useSelector } from 'react-redux';
import { useList } from 'react-use';

import {
  getProductGroups,
  getProductGroupsTreeOrdered,
} from 'reducers/productGroupsDB';
import { Config, ReceiptFooter } from '../Configuration';

const useStyles = makeStyles<Theme>(theme => ({
  header: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: '1.15rem',
  },
  buttonContainer: {
    display: 'flex',
    gap: '1rem',
  },
  grid: {
    width: '100%',
    marginTop: '1rem',
    display: 'grid',
    gridGap: '1rem',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
  },
  list: {
    height: '245px',
    marginTop: '1rem',
    overflowY: 'auto',
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: theme.palette.divider,
  },
  textarea: {
    '& textarea': {
      height: '275px !important',
    },
  },
  menuItemIcon: {
    marginLeft: '1rem',
  },
  helperTextIcon: {
    marginRight: '0.5rem',
  },
  listItemIcon: {
    minWidth: 0,
    marginRight: '1rem',
  },
}));

interface Props {
  footers: Config['receiptFooters'];
  selectedFooterId: string | null;
  onClose(): void;
  onSave(footer: ReceiptFooter): void;
}
function ReceiptFooterForm({
  footers,
  selectedFooterId,
  onClose,
  onSave,
}: Props) {
  const styles = useStyles();

  const selectedFooter = selectedFooterId
    ? footers?.find(footer => footer.id === selectedFooterId)
    : undefined;

  const productGroupsTree = useSelector(getProductGroupsTreeOrdered);
  const productGroups = useSelector(getProductGroups);

  const [footerText, setFooterText] = useState<string>(
    selectedFooter?.footerText ?? '',
  );
  const [
    productGroupIds,
    { push: pushProductGroupId, filter: filterProductGroupIds },
  ] = useList<string>(selectedFooter?.productGroupIds ?? []);

  const otherFooters = footers?.filter(
    footer => footer.id !== selectedFooterId,
  );

  function checkIsGroupAssignedToAnotherFooter(id: string) {
    return otherFooters?.some(footer => footer.productGroupIds.includes(id));
  }

  const selectedProductGroups = productGroups.filter(group =>
    productGroupIds.includes(group.id),
  );

  const hasInvalidProductGroups = selectedProductGroups.some(group =>
    checkIsGroupAssignedToAnotherFooter(group.id),
  );

  return (
    <div>
      <div className={styles.header}>
        <Typography variant="h6" className={styles.title}>
          {selectedFooter ? 'Edit footer' : 'Create new footer'}
        </Typography>
        <div className={styles.buttonContainer}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disableElevation
            disabled={
              !footerText.length ||
              !productGroupIds.length ||
              hasInvalidProductGroups
            }
            onClick={() => {
              onSave({
                id: selectedFooter?.id ?? uuidv4(),
                footerText,
                productGroupIds,
              });
              onClose();
            }}
          >
            Save
          </Button>
        </div>
      </div>
      <div className={styles.grid}>
        <div>
          <FormControl
            variant="outlined"
            fullWidth
            required
            error={hasInvalidProductGroups || !productGroupIds.length}
          >
            <InputLabel>Select product groups</InputLabel>
            <Select
              multiple
              fullWidth
              label="Select product groups"
              value={[]}
              onChange={e => {
                const isGroupAssignedToSelectedFooter = productGroupIds.includes(
                  String(e.target.value),
                );
                if (isGroupAssignedToSelectedFooter) {
                  filterProductGroupIds(id => id !== String(e.target.value));
                } else {
                  pushProductGroupId(String(e.target.value));
                }
              }}
            >
              {productGroupsTree.map(group => {
                const isGroupAssignedToSelectedFooter = productGroupIds.includes(
                  String(group.value),
                );
                const isGroupAssignedToAnotherFooter = checkIsGroupAssignedToAnotherFooter(
                  String(group.value),
                );
                return (
                  <MenuItem key={group.value} value={group.value}>
                    <Checkbox checked={isGroupAssignedToSelectedFooter} />
                    <ListItemText>{group.name}</ListItemText>
                    {isGroupAssignedToSelectedFooter &&
                    isGroupAssignedToAnotherFooter ? (
                      <ErrorOutline
                        color="error"
                        className={styles.menuItemIcon}
                      />
                    ) : null}
                  </MenuItem>
                );
              })}
            </Select>
            {hasInvalidProductGroups ? (
              <FormHelperText error>
                <ErrorOutline color="error" className={styles.helperTextIcon} />
                Some of the selected groups are already assigned to another
                footer
              </FormHelperText>
            ) : null}
            {!productGroupIds.length ? (
              <FormHelperText error>
                Select at least 1 product group
              </FormHelperText>
            ) : null}
          </FormControl>
          <List
            className={styles.list}
            subheader={
              <>
                <ListSubheader>Selected product groups</ListSubheader>
                <Divider />
              </>
            }
          >
            {selectedProductGroups.map(group => {
              const isGroupAssignedToAnotherFooter = checkIsGroupAssignedToAnotherFooter(
                group.id,
              );
              return (
                <>
                  <ListItem>
                    <ListItemText primary={group.name} />
                    {isGroupAssignedToAnotherFooter ? (
                      <ListItemIcon className={styles.listItemIcon}>
                        <ErrorOutline color="error" />
                      </ListItemIcon>
                    ) : null}
                    <ListItemSecondaryAction>
                      <IconButton
                        onClick={() =>
                          filterProductGroupIds(id => id !== group.id)
                        }
                      >
                        <Clear />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                </>
              );
            })}
          </List>
        </div>
        <div>
          <TextField
            className={styles.textarea}
            type="text"
            fullWidth
            variant="outlined"
            label="Footer text"
            required
            error={!footerText?.length}
            multiline
            minRows={16}
            maxRows={16}
            value={footerText}
            onChange={e => setFooterText(e.target.value)}
          />
        </div>
      </div>
    </div>
  );
}

export default ReceiptFooterForm;
